import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSessions, getTerms, getClasses, getBroadsheet, getAnnualResults,
  refreshBroadsheetPositions, getResultTemplates, exportResults,
} from '@/api/owner';
import {
  Grid, Download, RefreshCw, AlertCircle, Loader2, GraduationCap, FileText,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { resolveStorageUrl } from '@/utils/storage';
import { openExternal } from '@/lib/native';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;
const num = (n?: any) => (n == null || n === '' ? '—' : Number(n).toFixed(1));

type Mode = 'termly' | 'annual';

export function BroadsheetPage() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>('termly');
  const [sessionId, setSessionId] = useState<number | ''>('');
  const [termId, setTermId] = useState<number | ''>('');
  const [classId, setClassId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<number | ''>('');

  const sessionsQ = useQuery({ queryKey: ['owner-sessions'], queryFn: () => getSessions().then(unwrap) });
  const termsQ = useQuery({ queryKey: ['owner-terms'], queryFn: () => getTerms().then(unwrap) });
  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses({ per_page: 200 }).then(unwrap) });
  const templatesQ = useQuery({ queryKey: ['owner-result-templates'], queryFn: () => getResultTemplates().then(unwrap) });

  const sessions = arr<any>(sessionsQ.data);
  const terms = arr<any>(termsQ.data);
  const classes = arr<any>(classesQ.data);
  const templates = arr<any>(templatesQ.data);

  const visibleTerms = useMemo(
    () => sessionId ? terms.filter((t: any) => (t.session_id ?? t.academic_session_id) === Number(sessionId)) : terms,
    [terms, sessionId]
  );

  useEffect(() => {
    if (!sessionId && sessions.length > 0) {
      const cur = sessions.find((s: any) => s.is_current) ?? sessions[0];
      setSessionId(cur.id);
    }
  }, [sessions, sessionId]);

  useEffect(() => {
    if (mode !== 'termly') return;
    if (!termId && visibleTerms.length > 0) {
      const cur = visibleTerms.find((t: any) => t.is_current);
      if (cur) setTermId(cur.id);
    }
    if (termId && !visibleTerms.some((t: any) => t.id === termId)) setTermId('');
  }, [mode, visibleTerms, termId]);

  useEffect(() => {
    if (!templateId && templates.length > 0) setTemplateId(templates[0].id);
  }, [templates, templateId]);

  const enabled = mode === 'annual' ? !!classId && !!sessionId : !!classId && !!termId;

  const termlyQ = useQuery({
    queryKey: ['broadsheet-termly', classId, termId],
    queryFn: () => getBroadsheet({ class_id: Number(classId), term_id: Number(termId) }).then(unwrap),
    enabled: enabled && mode === 'termly',
  });
  const annualQ = useQuery({
    queryKey: ['broadsheet-annual', classId, sessionId],
    queryFn: () => getAnnualResults({ class_id: Number(classId), session_id: Number(sessionId) }).then(unwrap),
    enabled: enabled && mode === 'annual',
  });

  const termly = termlyQ.data;
  const annualRows = arr<any>(annualQ.data);
  const rows = arr<any>(termly?.rows);
  const subjects = arr<any>(termly?.subjects);

  const refreshPositionsM = useMutation({
    mutationFn: () => refreshBroadsheetPositions({ class_id: Number(classId), term_id: Number(termId) }),
    onSuccess: (r: any) => {
      const d = unwrap(r);
      alert(`Positions refreshed for ${d?.students ?? 'all'} student(s).`);
      qc.invalidateQueries({ queryKey: ['broadsheet-termly', classId, termId] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Refresh failed'),
  });

  const exportM = useMutation({
    mutationFn: () => {
      if (!templateId) throw new Error('Pick a template first.');
      return exportResults({
        template_id: Number(templateId),
        class_id: Number(classId),
        term_id: Number(termId),
        type: 'class',
      });
    },
    onSuccess: (r: any) => {
      const d = unwrap(r);
      const path = d?.path;
      if (path) void openExternal(resolveStorageUrl(path));
      else setError('Export returned no path.');
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? e?.message ?? 'Export failed'),
  });

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Assessments & Results</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Broadsheet</h1>
          <p className="text-slate-500 text-sm mt-1">
            {mode === 'termly' ? 'All subjects × all students for a class and term' : 'Annual ranking across 3 terms'}
          </p>
        </div>
        <div className="flex gap-2">
          {mode === 'termly' && enabled && (
            <button onClick={() => refreshPositionsM.mutate()}
              disabled={refreshPositionsM.isPending}
              className="px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1">
              <RefreshCw className="w-4 h-4" />
              {refreshPositionsM.isPending ? 'Refreshing…' : 'Refresh positions'}
            </button>
          )}
          {mode === 'termly' && enabled && (
            <button onClick={() => exportM.mutate()}
              disabled={!templateId || exportM.isPending || rows.length === 0}
              className="px-3 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-1">
              <Download className="w-4 h-4" />
              {exportM.isPending ? 'Exporting…' : 'Export class PDF'}
            </button>
          )}
        </div>
      </div>

      {/* Mode tabs */}
      <div className="inline-flex bg-slate-100 rounded-lg p-1">
        <button onClick={() => setMode('termly')}
          className={`px-4 py-1.5 text-sm font-bold rounded-md transition ${
            mode === 'termly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
          }`}>
          <FileText className="w-3.5 h-3.5 inline mr-1" /> Termly
        </button>
        <button onClick={() => setMode('annual')}
          className={`px-4 py-1.5 text-sm font-bold rounded-md transition ${
            mode === 'annual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
          }`}>
          <GraduationCap className="w-3.5 h-3.5 inline mr-1" /> Annual
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Session *</label>
            <select value={sessionId} onChange={(e) => { setSessionId(e.target.value ? Number(e.target.value) : ''); setTermId(''); }}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose session…</option>
              {sessions.map((s: any) => <option key={s.id} value={s.id}>{s.name}{s.is_current ? ' (active)' : ''}</option>)}
            </select>
          </div>
          {mode === 'termly' && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Term *</label>
              <select value={termId} onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : '')}
                disabled={!sessionId}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-50">
                <option value="">Choose term…</option>
                {visibleTerms.map((t: any) => <option key={t.id} value={t.id}>{t.name}{t.is_current ? ' (current)' : ''}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Class *</label>
            <select value={classId} onChange={(e) => setClassId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose class…</option>
              {classes.map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.arm ? ` · ${c.arm}` : ''}</option>)}
            </select>
          </div>
          {mode === 'termly' && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Export template</label>
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Choose template…</option>
                {templates.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {!enabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
          {mode === 'annual'
            ? 'Pick a session and a class.'
            : 'Pick a session, term, and class.'}
        </div>
      )}

      {/* TERMLY */}
      {mode === 'termly' && enabled && (
        termlyQ.isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto block" />
        ) : termlyQ.isError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
            Could not load broadsheet: {(termlyQ.error as any)?.response?.data?.message ?? 'Unknown error'}
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
            <Grid className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="font-semibold text-slate-700">No broadsheet yet</p>
            <p className="text-sm text-slate-500 mt-1">
              Compute results for this class + term first.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Students" value={rows.length} />
              <StatCard label="Highest avg" value={num(termly?.stats?.highest)} />
              <StatCard label="Lowest avg" value={num(termly?.stats?.lowest)} />
              <StatCard label="Class average" value={num(termly?.stats?.class_average)} />
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b-2 border-slate-300">
                  <tr>
                    <th className="px-2 py-3 text-left w-8">#</th>
                    <th className="px-2 py-3 text-left min-w-[160px]">Student</th>
                    <th className="px-2 py-3 text-left w-20">Adm. No.</th>
                    {subjects.map((sub: any) => (
                      <th key={sub.id} className="px-2 py-3 text-center w-20">
                        <div>{sub.code ?? sub.name}</div>
                        <div className="text-[9px] font-normal text-slate-400 normal-case">{sub.name}</div>
                      </th>
                    ))}
                    <th className="px-2 py-3 text-right w-20">Total</th>
                    <th className="px-2 py-3 text-right w-20">Avg</th>
                    <th className="px-2 py-3 text-right w-16">Pos</th>
                    <th className="px-2 py-3 text-center w-14">Grade</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r: any, idx: number) => (
                    <tr key={r.result_id} className={r.held ? 'bg-amber-50/40' : 'hover:bg-slate-50'}>
                      <td className="px-2 py-2 text-xs text-slate-500 font-mono">{idx + 1}</td>
                      <td className="px-2 py-2 font-medium text-slate-900 whitespace-nowrap">
                        {r.name}
                        {r.held && <span className="ml-1 text-[10px] font-bold text-amber-700">HOLD</span>}
                      </td>
                      <td className="px-2 py-2 font-mono text-xs text-slate-500">{r.admission_no}</td>
                      {subjects.map((sub: any) => {
                        const cell = r.subjects?.[sub.id];
                        return (
                          <td key={sub.id} className="px-2 py-2 text-center font-mono text-xs">
                            {cell?.total != null ? (
                              <>
                                {num(cell.total)}
                                <span className="ml-1 text-slate-400 text-[10px]">
                                  {cell.grade ?? ''}
                                </span>
                              </>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-2 py-2 text-right font-mono font-bold text-slate-900">{num(r.total)}</td>
                      <td className="px-2 py-2 text-right font-mono">{num(r.average)}</td>
                      <td className="px-2 py-2 text-right font-mono">{r.position ?? '—'}</td>
                      <td className="px-2 py-2 text-center font-bold">{r.grade ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 text-right">
              Print layout: A3 landscape. Hover a column to see full subject name.
            </p>
          </>
        )
      )}

      {/* ANNUAL */}
      {mode === 'annual' && enabled && (
        annualQ.isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto block" />
        ) : annualQ.isError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
            Could not load annual: {(annualQ.error as any)?.response?.data?.message ?? 'Unknown error'}
          </div>
        ) : annualRows.length === 0 ? (
          <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
            <GraduationCap className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="font-semibold text-slate-700">No annual results computed yet</p>
            <p className="text-sm text-slate-500 mt-1">
              Go to Results → Annual tab and compute the annual first.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="px-3 py-3 text-left w-10">#</th>
                  <th className="px-3 py-3 text-left">Student</th>
                  <th className="px-3 py-3 text-left">Adm. No.</th>
                  <th className="px-3 py-3 text-right">Total</th>
                  <th className="px-3 py-3 text-right">Average</th>
                  <th className="px-3 py-3 text-right">Position</th>
                  <th className="px-3 py-3 text-center">Grade</th>
                  <th className="px-3 py-3 text-left">Promotion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {annualRows.map((r: any, idx: number) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-xs text-slate-500 font-mono">{idx + 1}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">{r.name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.admission_no}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{num(r.total)}</td>
                    <td className="px-3 py-2 text-right font-mono">{num(r.average)}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.position ?? '—'}</td>
                    <td className="px-3 py-2 text-center font-bold">{r.grade ?? '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full border ${
                        r.promotion_status === 'promoted' ? 'bg-green-50 text-green-700 border-green-200' :
                        r.promotion_status === 'repeat'   ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        r.promotion_status === 'graduate' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        'bg-slate-100 text-slate-600 border-slate-200'
                      }`}>
                        {r.promotion_status ?? '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: any }) {
  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      <p className="text-2xl font-extrabold text-slate-900 mt-1">{value}</p>
    </div>
  );
}
